<template>
    <placement-sets v-if="hasSets" :item-data="itemData"></placement-sets>

    <div v-for="(placement, placementIndex) in visiblePlacements"
        :key="placement.code"
        class="px-[20px] lg:px-[40px]"
    >
        <div class="mt-[29.73px]"></div>
        <div :class="{
            'border-t border-gray-light my-8 -mx-12': placementIndex > 0,
        }"></div>
        <placement-header :placement="placement" :index="placementIndex"></placement-header>

        <step-options-standard-buttons v-if="placement.groupType === 'standard-buttons'"
            :item-data="itemData"
            :placement="placement"
        ></step-options-standard-buttons>

        <template v-if="placement.groupType === 'colors-with-header'">
            <step-options-initials-buttons
                :key="'FlightLines'"
                :main-placement="placement"
                :option="{
                    code: 'FlightLines',
                    label: 'Flight Lines',
                    selected: isSelectedFlightLines,
                    value: 'FlightLines',
                    raw: getFlightLinesYes(placement),
                }"
                @select="() => selectFlightLines(placement)"
                :has-error-state="localErrorState"
            >
                <template v-if="isSelectedFlightLines">
                    <span
                        class="text-sm font-semibold font-oceanside text-ink"
                    >
                        {{ $t('WHY_USE_FLIGHTLINES') }}
                    </span>
                    <div class="text-sm font-oceanside leading-[20px]">
                        {{ $t('WHY_USE_FLIGHTLINES_COPY') }}
                        <a
                            href="https://www.youtube.com/watch?v=OrRF_KNeqH4"
                            target="_blank"
                            class="text-sm font-oceanside text-red underline"
                        >
                            {{ $t('LEARN_MORE') }}
                        </a>
                    </div>
                </template>
                <initials-colors
                    v-if="isSelectedFlightLines"
                    :placement="placement"
                    :colors-placement="placement.code"
                    :colors="getColorOptions(placement)"
                    @select="(color) => selectColor(placement, color)"
                />
            </step-options-initials-buttons>

            <!-- No Thanks Button -->
            <step-options-initials-buttons
                :key="getFlightLinesNoThanks(placement)?.code"
                :main-placement="placement"
                :option="{
                    code: getFlightLinesNoThanks(placement)?.code,
                    label: 'No Flight Lines',
                    raw: getFlightLinesNoThanks(placement),
                    selected: placement.selectedValue === getFlightLinesNoThanks(placement)?.code,
                    value: getFlightLinesNoThanks(placement)?.code
                }"
                @unselect="() => selectFlightLinesNoThanks(placement)"
                @select="() => selectFlightLinesNoThanks(placement)"
                :has-error-state="localErrorState"
            />
        </template>

        <step-options-textarea v-if="placement.groupType === 'textarea'"
            :placement="placement"
        ></step-options-textarea>

        <step-options-black-white-buttons v-if="placement.groupType === 'black-white-buttons'"
            :placement="placement"
        ></step-options-black-white-buttons>

        <step-options-black-white-buttons-grid v-if="placement.groupType === 'black-white-buttons-grid'"
            :placement="placement"
        ></step-options-black-white-buttons-grid>

        <step-options-dropdowns v-if="placement.groupType === 'dropdowns'"
            :placement="placement"
        ></step-options-dropdowns>

        <!-- <placement-standard :placement="placement"></placement-standard> -->
    </div>
</template>

<script>
    import StepOptionsStandardButtons from './StepOptionsStandardButtons.vue';
    import StepOptionsDropdowns from '../StepOptionsDropdowns.vue';
    import StepOptionsTextarea from '../StepOptionsTextarea.vue';
    import PlacementHeader from '../PlacementHeader.vue';
    import StepOptionsBlackWhiteButtons from './StepOptionsBlackWhiteButton.vue';
    import StepOptionsBlackWhiteButtonsGrid from './StepOptionsBlackWhiteButtonGrid.vue';
    // import PlacementStandard from '../PlacementStandard.vue';
    import PlacementSets from '../PlacementSets.vue';
    import StepOptionsInitialsButtons from './StepOptionsInitialsButtons.vue';
    import InitialsColors from '../InitialsColors.vue';

    import allStores from '../../stores/all';

    export default {
        mixins: [allStores],

        components: {
            StepOptionsStandardButtons,
            StepOptionsDropdowns,
            PlacementHeader,
            // PlacementStandard,
            PlacementSets,
            StepOptionsTextarea,
            StepOptionsBlackWhiteButtons,
            StepOptionsBlackWhiteButtonsGrid,
            StepOptionsInitialsButtons,
            InitialsColors,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                localErrorState: false,
            };
        },

        computed: {
            isSelectedFlightLines() {
                return this.visiblePlacements.some((placement) => placement.selectedValue
                    && placement.selectedValue !== this.getFlightLinesNoThanks(placement)?.code
                    && placement.selectedValue !== 'FlightLine-FlightLine-Blank');
            },

            isCompleteSelection() {
                return true;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visiblePlacements() {
                const placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                const visible = placements
                    .filter((placement) => placement.code !== 'Ferrule')
                    .map((placement) => {
                        const selected = this.viewController.selectedAtPlacement(placement.code);

                        // Add extra properties to each placement:
                        // - a code of the currently selected component.
                        return {
                            selectedValue: selected ? selected.code : null,
                            groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                            ...placement,
                        };
                    });

                return visible;
            },

            hasSets() {
                return this.itemData.custom['v-sets'];
            },
        },

        methods: {
            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            handleApplySelections() {
                this.setShowGroupOverlay(false);
                this.$emit('applySelections');
            },

            getPlacementVariant(placement) {
                return placement?.custom?.['v-placement-variant'];
            },

            getColorOptions(placement) {
                if (!placement?.components || !this.customizerStore.isFinishedLoading) {
                    return [];
                }

                const filtered = placement.components?.filter(
                    (c) => !c.placeholder,
                )?.map((c) => ({
                    code: c.code,
                    name: c.description[0].name,
                    hex: c.custom?.['v-hex'],
                    image: c.custom?.['v-image'],
                    selected: placement.selectedValue === c.code,
                }));

                return filtered;
            },

            selectValue(placement, option) {
                this.customizerStore.setSelection(placement.code, option.value, false);
            },

            getFlightLinesNoThanks(placement) {
                return placement.components?.find((c) => c.placeholder && c.placeholderOptional);
            },

            getFlightLinesYes(placement) {
                // Just grab a first option.
                return placement.components?.find((c) => !c.placeholder);
            },

            selectFlightLinesNoThanks(placement) {
                const noThanksOption = this.getFlightLinesNoThanks(placement);

                if (noThanksOption) {
                    this.customizerStore.setSelection(placement.code, noThanksOption.code, false);
                }
            },

            selectFlightLines(placement) {
                const firstColor = this.getColorOptions(placement)?.[0];
                if (firstColor) {
                    this.customizerStore.setSelection(placement.code, firstColor.code, false);
                }
            },

            selectColor(placement, color) {
                this.customizerStore.setSelection(placement.code, color.code, false);
            },

            resetFlightLines() {
                this.customizerStore.selectBlankFlightLines();
            },
        },
    };
</script>
